//------------------------------------------------------------------------
// Basic form validation
//
// Usage Info:
// • Add “js-validate” to <form> or other wrapper
//   - Add “data-form-ajax” to submit via AJAX
// • Add appropriate data-validate value to each input
//   - data-validate="email"
//   - data-validate="phone"
//   - data-validate="number"
//   - data-validate="zip"
//   - data-validate="notempty"
// • Add error message markup inside of <label>
//     <span class="is-hidden" data-validate="error" role="alert">Please enter a valid email address</span>
// • Add `data-validate="submit"` to submit button
//     <button type="submit" data-validate="submit">Submit</button>
// • Required fields should use `aria-required="true"` when possible, but `required` and `data-validate-required` will also work
//------------------------------------------------------------------------
'use strict';
import $ from 'jquery';
import Backbone from 'backbone';

var ValidateForm = Backbone.View.extend({
  events: {
    'blur [data-validate]': 'validate',// check for errors on blur
    'change [data-validate]': 'validate',// for selects, radios, and checkboxes
    'input input[data-validate]': 'clearError',// hide error while typing
    'input textarea[data-validate]': 'clearError',// hide error while typing
    'keypress [data-validate="number"]': 'numberEvent',// prevent non-number characters
    'submit': 'submitHandler'// validate prior to submitting form
  },

  initialize: function() {
    var self = this;

    // Add the “novalidate” attribute to form to disable default browser error messages if using “required” attribute
    // Note: `prop('novalidate', true)` doesn't work for some reason ¯\_(ツ)_/¯
    this.$el.attr('novalidate', 'novalidate');

    // Find error messages
    this.$errorMsg = this.$el.find('[data-validate="error"]');

    // Submit button
    this.$submit = this.$el.find('[data-validate="submit"]');
    this.submitText = this.$submit.text();// initial text
    this.submittedText = this.$submit.data('validate-success') || false;// text to show on submit

    // Check if we should submit via AJAX
    this.isAjaxSubmit = this.$el[0].hasAttribute('data-form-ajax');

    // Elements that require validation, set to `aria-invalid="true"` on load
    this.$validateEls = this.$el.find('[data-validate]').not(this.$submit).not(this.$errorMsg);

    // Validating every possible email is a fool’s errand and not necessary.
    // Instead, just check for at least one “@” and “.”
    // https://davidcel.is/posts/stop-validating-email-addresses-with-regex/
    this.emailRegex =  /^.+@.+\..+$/;

    // If more validation is desired, we can check for only one “@”
    // Based on https://github.com/plataformatec/devise/blob/593ae41f9dac165a404b05cd3abd959245c64908/lib/devise.rb#L109-L113
    // this.emailRegex =  /^[^@\s]+@([^@\s]+\.)+[^@\s]+$/;

    // Numeric value test (allows single decimal)
    this.numericRegex = /^((\d+)|(\.\d+)|(\d+\.\d+))%?$/;

    // Phone number test (very loose, allows everything except line breaks)
    this.phoneRegex = /^.+$/;

    // Postal code RegEx, allows from 2–32 letters, numbers, spaces, or dashes
    this.zipRegex = /^[\w\d\- ]{2,32}$/;

    // Ensures that data-hide-message-delay setTimeout doesn't stack on each other
    this.timerIdShowMsg = null;
    this.timerIdErrorMsg = null;

    // Using the back button in Firefox and Safari displays a cached page with the submit button still disabled, so we have to manually reenable it.
    // https://bugzilla.mozilla.org/show_bug.cgi?id=443289
    // http://stackoverflow.com/a/13123626/
    $(window).on('pageshow', function(evt) {
      if ( evt.originalEvent.persisted ) {
        self.$submit.prop('disabled', false).text(self.submitText);
      }
    });
  },

  isEmail: function(str) {
    return this.emailRegex.test(str);
  },

  isNumeric: function(str) {
    return this.numericRegex.test(str);
  },

  isPhone: function(str) {
    return this.phoneRegex.test(str);
  },

  isZip: function(str) {
    return this.zipRegex.test(str);
  },

  isEmpty: function($el) {
    // Check if checkbox or radio button
    if ( $el.is('[type="checkbox"]') || $el.is('[type="radio"]') ) {
      return !$el.is(':checked');
    }
    // For all other elements, get the value
    else {
      return !$el.val();
    }
  },

  isRequired: function($el) {
    // We can’t apply the “required” attribute or “aria-required="true"” on checkbox inputs so we have to use a custom attribute
    // https://github.com/GoogleChrome/accessibility-developer-tools/issues/283
    return $el.is('[aria-required="true"]') || $el.is('[required]') || $el.is('[data-validate-required]');
  },

  numberEvent: function(evt) {
    // Get typed character
    var key = String.fromCharCode(evt.which);
    var allowedChars = /[,.0-9%]/;

    // Don't allow non-number characters to be entered
    if ( !allowedChars.test(key) ) {
      evt.preventDefault();
    }
  },

  // Test if element is valid and toggle error msg
  validateEl: function($el) {
    var type = $el.data('validate');
    var val = $el.val();

    // Email
    if ( type === 'email' ) {
      this.toggleErrorMsg(this.isEmail(val), $el);
    }

    // Numbers (allows single decimal and/or % sign)
    else if ( type === 'number' ) {
      // Strip commas
      val = val.replace(/,/g, '');
      $el.val(val);
      this.toggleErrorMsg(this.isNumeric(val), $el);
    }

    // Phone number (allows everything except line breaks)
    else if ( type === 'phone' ) {
      this.toggleErrorMsg(this.isPhone(val), $el);
    }

    // ZIP code (from 2–12 letters, numbers, dashes, or spaces)
    else if ( type === 'zip' ) {
      this.toggleErrorMsg(this.isZip(val), $el);
    }

    // Not empty
    else if ( type === 'notempty' ) {
      // TODO: Allow on fieldsets containing radio buttons

      this.toggleErrorMsg(!this.isEmpty($el), $el);
    }
  },

  // Get associated label element
  getLabel: function($el) {
    return this.$el.find('[for="' + $el.attr('id') + '"]');
  },

  // Get error message element associated with form element
  getErrorMsg: function($el) {
    return this.getLabel($el).find('[data-validate="error"]');
  },

  showErrorMsg: function($el) {
    this.getErrorMsg($el).parent().removeClass('is-hidden');
    $el.addClass('is-invalid').attr('aria-invalid', 'true');
  },

  hideErrorMsg: function($el) {
    this.getErrorMsg($el).parent().addClass('is-hidden');
    $el.removeClass('is-invalid').attr('aria-invalid', 'false');
  },

  toggleErrorMsg: function(isValid, $el) {
    if ( isValid ) {
      this.hideErrorMsg($el);
    }
    else {
      this.showErrorMsg($el);
    }
  },

  clearError: function(evt) {
    var $target = $(evt.target);
    this.hideErrorMsg($target);
  },

  isFormValid: function() {
    // Check for required and invalid fields
    var $invalidEls = this.$validateEls.filter('[aria-invalid="true"]');

    return !$invalidEls.length;
  },

  validate: function(evt) {
    var $target = $(evt.target);

    // Exclude blank optional fields
    if ( this.isRequired($target) || !this.isEmpty($target) ) {
      this.validateEl( $(evt.target) );
    }
  },

  validateAll: function() {
    var self = this;

    this.$validateEls.each( function() {
      var $this = $(this);
      // Exclude blank optional fields
      if ( self.isRequired($this) || !self.isEmpty($this) ) {
        self.validateEl($this);
      }
    });
  },

  // Submit form data via XMLHttpRequest()
  // https://developer.mozilla.org/en-US/docs/Learn/HTML/Forms/Sending_forms_through_JavaScript
  sendData: function() {
    var self = this;

    var XHR = new XMLHttpRequest();
    // Use FormData interface (IE 10+)
    // https://developer.mozilla.org/en-US/docs/Web/API/FormData
    var FD = new FormData(this.el);

    // console.log([...FD]);

    // Define what happens on successful data submission
    XHR.addEventListener('load', function () {
      // 'load' fires for ANY completed response, including 4xx/5xx — only treat
      // 2xx as success; route everything else to the error UI.
      if (XHR.status < 200 || XHR.status >= 300) {
        var errorShow = self.el.getAttribute('data-error-show');
        var successShow = self.el.getAttribute('data-success-show');
        var hideMessageDelay = self.el.getAttribute('data-hide-message-delay'); // this is in miliseconds

        if (errorShow) {
          document.querySelectorAll(errorShow).forEach(el => (el.style.display = 'block'));
        }

        // A new error message makes any pending success message stale; hide it immediately
        if (successShow) {
          clearTimeout(self.timerIdShowMsg);
          document.querySelectorAll(successShow).forEach(el => (el.style.display = 'none'));
        }

        if (hideMessageDelay && errorShow) {
          clearTimeout(self.timerIdErrorMsg); // remove the past one
          self.timerIdErrorMsg = setTimeout(function() {
            document.querySelectorAll(errorShow).forEach(el => el.style.display = 'none');
          }, parseInt(hideMessageDelay, 10));
        }

        self.resetSubmitButton();
        console.error('Form submission failed (' + XHR.status + ') for ' + self.el.getAttribute('action'));
        return;
      }

      var successShow = self.el.getAttribute('data-success-show');
      var successHide = self.el.getAttribute('data-success-hide');
      var errorShow = self.el.getAttribute('data-error-show');
      var hideMessageDelay = self.el.getAttribute('data-hide-message-delay'); // this is in miliseconds

      // Hide elements on success
      if (successHide) {
        document.querySelectorAll(successHide).forEach(el => (el.style.display = 'none'));
      }

      // Show elements on success
      if (successShow) {
        document.querySelectorAll(successShow).forEach(el => (el.style.display = 'block'));
      }

      // A new success message makes any pending error message stale; hide it immediately
      if (errorShow) {
        clearTimeout(self.timerIdErrorMsg);
        document.querySelectorAll(errorShow).forEach(el => (el.style.display = 'none'));
      }

      if (hideMessageDelay && successShow) {
        clearTimeout(self.timerIdShowMsg); // remove the past one
        self.timerIdShowMsg = setTimeout(function() {
          document.querySelectorAll(successShow).forEach(el => el.style.display = 'none');
        }, parseInt(hideMessageDelay, 10));
      }

      // Reset form fields
      let inputEls = self.el.querySelectorAll('input');
      let textareas = self.el.querySelectorAll('textarea');

      let allInputs = Array.from(inputEls)
        .concat(Array.from(textareas))
        .filter(el => el.getAttribute('type') !== 'hidden' || el.getAttribute('type') !== 'submit');

      // Reset form fields
      allInputs.forEach(function (el) {
        el.value = '';
        el.checked = false;
      });

      self.resetSubmitButton();
    });

    // Define what happens in case of error
    XHR.addEventListener('error', function () {
      var errorShow = self.el.getAttribute('data-error-show');
      var successShow = self.el.getAttribute('data-success-show');
      var hideMessageDelay = self.el.getAttribute('data-hide-message-delay');

      // Show elements on success
      if (errorShow) {
        document.querySelectorAll(errorShow).forEach(el => (el.style.display = 'block'));
      }

      // A new error message makes any pending success message stale; hide it immediately
      if (successShow) {
        clearTimeout(self.timerIdShowMsg);
        document.querySelectorAll(successShow).forEach(el => (el.style.display = 'none'));
      }

      if (hideMessageDelay && errorShow) {
        clearTimeout(self.timerIdErrorMsg); // remove the past one
        self.timerIdErrorMsg = setTimeout(function() {
          document.querySelectorAll(errorShow).forEach(el => el.style.display = 'none');
        }, parseInt(hideMessageDelay, 10));
      }

      self.resetSubmitButton();

      console.error('Unable to post form data to ' + self.el.getAttribute('action'));
    });

    // Set up our request
    XHR.open('POST', this.el.getAttribute('action'));

    // Send Rails CSRF token so same-origin POSTs pass verify_authenticity_token
    var csrfMeta = document.querySelector('meta[name="csrf-token"]');
    if (csrfMeta) {
      XHR.setRequestHeader('X-CSRF-Token', csrfMeta.getAttribute('content'));
    }

    // Send our FormData object; HTTP headers are set automatically
    XHR.send(FD);
  },

  resetSubmitButton: function() {
    this.$submit.removeAttr('disabled');
    this.$submit.removeAttr('aria-busy');
  },

  // Don't allow submit if validation errors are present
  submitHandler: function(evt) {
    var self = this;

    // Validate all elements
    this.validateAll();

    // If form is invalid, scroll to first error message
    if ( !this.isFormValid() ) {
      // Prevent form submission
      evt.preventDefault();

      // Find first error message
      var $errMsg = this.getLabel($('[aria-invalid="true"]')).first();

      // Scroll to first error
      $errMsg[0].scrollIntoView();
    }
    // Form is valid
    else {
      // Disable submit button to prevent multiple submissions
      this.$submit.prop('disabled', true);
      this.$submit.attr('aria-busy', 'true');

      // Update submit button text if provided
      if ( !!this.submittedText ) {
        this.$submit.text( this.submittedText );
      }

      // Check if form should submit via AJAX
      if (this.isAjaxSubmit) {
        // Prevent form from submitting normally
        evt.preventDefault();
        this.sendData();
      }

      // Optional: Trigger “form-valid” event for other modules
      // Backbone.trigger('form-valid', $(evt.target));
    }
  }
});

$('.js-validate').each(function() {
  new ValidateForm({el: this});
});